/**
 * Resolving a host's wallet and subaccount name into the id every Nado query
 * and signature is keyed on — and naming the two ways that quietly goes wrong.
 */

import type { Address, Hex } from 'viem'

import type { FunLogger } from '../../src/utils/funLogger'
import { buildNadoSubaccountId } from './encoding'

/**
 * Nado reports a name it can't decode as UTF-8 as the hex of its bytes, so a
 * hex-shaped name means a decoded engine value reached us instead of the one
 * subaccount state holds. Those digits would pack as ASCII and address a
 * different subaccount, under the byte limit and without erroring.
 */
const HEX_SHAPED_SUBACCOUNT_NAME = /^(?:[0-9a-f]{2})+$/i

/**
 * Packed subaccount id, or undefined when one can't be built — the caller
 * disables the config rather than crashing the render.
 */
export function resolveNadoSubaccount({
  walletAddress,
  subaccountName,
  logger,
}: {
  walletAddress: Address | undefined
  subaccountName: string | undefined
  logger: FunLogger
}): Hex | undefined {
  if (!walletAddress) {
    return undefined
  }
  if (subaccountName && HEX_SHAPED_SUBACCOUNT_NAME.test(subaccountName)) {
    logger.warn('nado:withdrawal:hexShapedSubaccountName', {
      name: subaccountName,
    })
  }
  try {
    return buildNadoSubaccountId(walletAddress, subaccountName)
  } catch (err) {
    // Over 12 bytes — Nado can't represent it, so there's nothing to sign against.
    const error = err instanceof Error ? err : new Error(String(err))
    logger.error('nado:withdrawal:invalidSubaccountName', error, {
      name: subaccountName,
    })
    return undefined
  }
}
